/**
 * The figures a screen is about, in one row under its name.
 *
 * This is the one place the design contract allows a figure to sit in its own
 * bordered cell, and it is allowed there only under conditions — §16 still
 * refuses a card whose whole content is a number and a heading, wherever it
 * appears. The conditions are enforced by this component's shape rather than
 * by remembering them:
 *
 *   - at most four cells, because the strip states a subject and five subjects
 *     is none. The tuple type is what stops a fifth being added;
 *   - one lead cell at most, spanning two columns and taking the hero size, so
 *     the strip says which figure the screen is about;
 *   - every cell carries a second line. `note` is required, not optional: a
 *     comparison, a denominator, a limit bar or the date a figure settles is
 *     what makes it readable rather than decorative, and a figure with nothing
 *     true to put there does not belong in the strip.
 *
 * The strip is point-in-time. A period control never governs it — §11 — so
 * nothing accumulated over a range is rendered here.
 *
 * Contract §4.1.
 */

export type LeadCell = {
  readonly label: string;
  /** Already formatted by the presentation primitive for its metric. */
  readonly value: React.ReactNode;
  /** The second line. Required — see the header comment. */
  readonly note: React.ReactNode;
  /** The subject of the screen: two columns wide, hero-sized. One per strip. */
  readonly lead?: boolean;
  readonly testId?: string | undefined;
};

/** A cell that leads: two columns wide and hero-sized. */
type Lead = LeadCell & { readonly lead: true };
/** A cell of ordinary width. */
type Plain = LeadCell & { readonly lead?: false };

/**
 * The strip is four columns wide and the shapes that fill it exactly.
 *
 * Written as a union of tuples rather than an array because the two rules that
 * matter here — at most four columns, and a column count that divides the
 * figures exactly (§4) — are the two a component cannot enforce at runtime
 * without shipping a broken row first. This is the constraint that caught the
 * first attempt: a lead plus three ordinary cells is five columns, and the
 * fifth wrapped onto its own line under a quarter-width box.
 */
type Cells =
  | readonly [Lead, Plain, Plain]
  | readonly [Lead, Plain]
  | readonly [Plain, Plain]
  | readonly [Plain, Plain, Plain]
  | readonly [Plain, Plain, Plain, Plain];

const COLUMNS: Record<number, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};

type Props = {
  readonly cells: Cells;
  readonly testId?: string | undefined;
};

export function LeadStrip({ cells, testId }: Props): React.JSX.Element {
  // The lead cell takes two columns, so the column count is the number of
  // cells plus one for it. That keeps the count dividing the figures exactly,
  // which is the rule a wrapped row breaks.
  const span = cells.reduce((total, cell) => total + (cell.lead === true ? 2 : 1), 0);
  // The tuple type above already keeps this between two and four; the fallback
  // exists so the class name is a string rather than a lookup that might not
  // be, and never because a fifth shape is expected.
  const columns = COLUMNS[span] ?? "md:grid-cols-4";

  return (
    <div className={`grid grid-cols-1 gap-2 ${columns}`} data-testid={testId}>
      {cells.map((cell) => (
        <Cell key={cell.label} {...cell} />
      ))}
    </div>
  );
}

function Cell({
  label,
  value,
  note,
  lead = false,
  testId,
}: LeadCell): React.JSX.Element {
  return (
    <div
      data-testid={testId}
      data-lead={lead ? "" : undefined}
      // Content grouped at the top, not spread over the cell. Justified
      // between, the three lines drifted apart to fill whatever height the
      // lead cell set, and a strip whose captions and figures sit at four
      // different heights is what "разбросано" describes.
      className={`flex flex-col rounded-panel border border-border-subtle bg-surface-default p-4 ${
        lead ? "md:col-span-2" : ""
      }`}
    >
      <p className="text-body-main font-medium text-text-secondary">{label}</p>
      <div
        className={`mt-2 text-text-primary ${
          lead ? "text-hero-metric-mobile sm:text-hero-metric" : "text-hero-metric-mobile"
        }`}
      >
        {value}
      </div>
      <div className="mt-1 text-body-main text-text-secondary">{note}</div>
    </div>
  );
}
